<script setup>
    import { thumbailForSpeaker } from '@/composables/helpers/speakers';

    const defaultSpeakerImg = '/assets/img/placeholder-speaker.jpg';

    const props = defineProps({
        speaker: {
            type: Object,
            required: true,
        },
        hide_featured: {
            type: Boolean,
            default: false,
        },
        theme: {
            type: String,
            default: 'light',
            validator: (v) => ['light', 'dark'].includes(v),
        },
        index: {
            type: Number,
            default: 0,
        },
        // Eager-load the headshot for cards rendered during SSR — they're
        // already on screen at hydration time, and lazy-loading them causes
        // a flash where the user sees the placeholder, then the image, then
        // the pop animation.
        eager: {
            type: Boolean,
            default: false,
        },
    });

    const isDark = computed(() => props.theme === 'dark');

    const HEX_MASKS = [
        '/img/2026/speakers/speaker-hex.svg',
        '/img/2026/speakers/hex-2.svg',
        '/img/2026/speakers/hex-3.svg',
        '/img/2026/speakers/hex-4.svg',
        '/img/2026/speakers/hex-5.svg',
    ];

    const maskUrl = computed(() => HEX_MASKS[props.index % HEX_MASKS.length]);

    const rootRef = ref(null);
    const imgRef = ref(null);
    const isVisible = ref(false);
    const isAnimated = ref(false);
    const imgLoaded = ref(false);
    let pendingReveal = false;

    const reveal = () => {
        if (imgLoaded.value) {
            isVisible.value = true;
        } else {
            pendingReveal = true;
        }
    };

    const onImgLoad = () => {
        imgLoaded.value = true;
        if (pendingReveal) {
            pendingReveal = false;
            isVisible.value = true;
        }
    };

    // If the image errors out we still need to release the card so it doesn't
    // stay invisible forever. The placeholder image will render in its place.
    const onImgError = onImgLoad;

    onMounted(() => {
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            isVisible.value = true;
            isAnimated.value = true;
            return;
        }
        const el = rootRef.value;
        if (!el) return;

        // If the image already loaded synchronously (cached / SSR-eager),
        // mark it so reveal() can flip isVisible immediately when IO fires.
        if (imgRef.value?.complete && imgRef.value?.naturalWidth) {
            imgLoaded.value = true;
        }

        const onEnd = () => {
            isAnimated.value = true;
            el.removeEventListener('animationend', onEnd);
        };
        el.addEventListener('animationend', onEnd);

        // Wait one frame for layout, then decide. If the card is already in
        // the viewport (typical SSR'd above-the-fold case), skip the pop
        // animation entirely so the user doesn't see a flash → hide →
        // animate sequence on hydration. Otherwise wire an IO that fires as
        // the row scrolls into view; the animation is still gated on the
        // headshot finishing load so cards don't pop in with a blank image.
        let io = null;
        const cleanup = () => {
            io?.disconnect();
            el.removeEventListener('animationend', onEnd);
        };
        requestAnimationFrame(() => {
            const rect = el.getBoundingClientRect();
            const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
            if (inViewport) {
                isVisible.value = true;
                isAnimated.value = true;
                return;
            }
            io = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        reveal();
                        io.disconnect();
                        break;
                    }
                }
            }, { rootMargin: '0px' });
            io.observe(el);
        });
        onUnmounted(cleanup);
    });

    const bio = computed(() => {
        if (!props.speaker.bio) return '';
        const plain = String(props.speaker.bio).replace(/<[^>]*>/g, '');
        if (plain.length <= 140) return plain;
        return plain.slice(0, 140).trimEnd() + '…';
    });

    const speakerHref = computed(() => {
        if (!props.speaker?.id && !props.speaker?.slug) return null;
        return `/speakers/${props.speaker.slug || props.speaker.id}`;
    });

    const speakerImage = computed(() => thumbailForSpeaker(props.speaker));
</script>

<template>
    <div ref="rootRef"
        class="speakers-grid-card flex flex-col gap-[20px]"
        :class="[
            isDark ? 'text-[var(--cream)]' : 'text-[var(--magenta)]',
            { 'is-visible': isVisible, 'is-animated': isAnimated },
        ]"
        :style="`--speaker-hex-mask: url('${maskUrl}');`">
        <div class="speaker-hex-container relative w-full aspect-square">
            <MessLink v-if="speakerHref"
                :href="speakerHref"
                class="speaker-hex-link group absolute inset-0 display-block overflow-hidden">
                <img ref="imgRef"
                    :src="speakerImage?.url || defaultSpeakerImg"
                    :alt="`${speaker.name} profile photo`"
                    :loading="eager ? 'eager' : 'lazy'"
                    :fetchpriority="eager ? 'high' : 'auto'"
                    @load="onImgLoad"
                    @error="onImgError"
                    class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
            </MessLink>
            <div v-else class="speaker-hex-link group absolute inset-0 display-block overflow-hidden">
                <img ref="imgRef"
                    :src="speakerImage?.url || defaultSpeakerImg"
                    :alt="`${speaker.name} profile photo`"
                    :loading="eager ? 'eager' : 'lazy'"
                    :fetchpriority="eager ? 'high' : 'auto'"
                    @load="onImgLoad"
                    @error="onImgError"
                    class="absolute inset-0 w-full h-full object-cover" />
            </div>

        </div>

        <div class="flex flex-col gap-[6px] md:gap-[8px]">
            <span v-if="!hide_featured && speaker.featured"
                class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase"
                :class="isDark ? 'text-[#FCD4E5]' : 'text-[var(--dark-pink)]'">
                Featured
            </span>

            <MessLink v-if="speakerHref"
                :href="speakerHref"
                class="speaker-name font-hubspot-serif font-normal text-[30px] leading-[1.25] tracking-[0] m-0 no-underline
                    [&_span]:leading-[1.25]
                    decoration-dotted decoration-[#FCC3DC] decoration-[1.5px] underline-offset-[5px]
                    [text-decoration-skip-ink:auto] [text-underline-position:from-font]
                    hover:underline"
                :class="isDark ? 'text-white' : 'text-[var(--magenta)]'">
                <span v-html="speaker.name" />
            </MessLink>
            <h3 v-else
                class="speaker-name font-hubspot-serif font-normal text-[30px] leading-[1.25] tracking-[0] m-0
                    [&_span]:leading-[1.25]"
                :class="isDark ? 'text-white' : 'text-[var(--magenta)]'">
                <span v-html="speaker.name" />
            </h3>

            <p v-if="speaker.job_title || speaker.company"
                class="font-hubspot-sans font-medium text-[12px] leading-[1.7] tracking-[0.84px] uppercase m-0"
                :class="isDark ? 'text-white' : 'text-[var(--magenta)]'">
                <span v-if="speaker.job_title" v-html="speaker.job_title"></span>
                <span v-if="speaker.company">&nbsp;@ {{ speaker.company }}</span>
            </p>

            <MessLink v-if="bio && speakerHref"
                :href="speakerHref"
                class="speaker-bio font-hubspot-sans font-light text-[14px] leading-[22px] tracking-[0.28px] m-0 line-clamp-3 no-underline pb-[2px]
                    decoration-dotted decoration-[#FCC3DC] decoration-[1px] underline-offset-[3px]
                    [text-decoration-skip-ink:auto] [text-underline-position:from-font]
                    hover:underline hover:text-white transition-colors"
                :class="isDark ? 'text-[#FCD4E5]' : 'text-[var(--magenta)]'">
                {{ bio }}
            </MessLink>
            <div v-else-if="bio"
                class="speaker-bio font-hubspot-sans font-light text-[14px] leading-[22px] tracking-[0.28px] m-0 line-clamp-3">
                {{ bio }}
            </div>
        </div>
    </div>
</template>

<style>
/* Cards start hidden and only promote to a GPU layer while animating.
   The `is-animated` class is set from animationend so the `transform`
   property is dropped entirely once the pop finishes — otherwise cards
   stay on their own composite layer forever and cause repaint flicker
   during page scroll. */
.speakers-grid-card:not(.is-visible) {
    transform-origin: center center;
    transform: scale(0.5);
    opacity: 0;
}

.speakers-grid-card.is-visible:not(.is-animated) {
    transform-origin: center center;
    animation: speakers-grid-card-pop 400ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.speakers-grid-card.is-animated {
    /* Intentionally no transform/opacity overrides — defaults apply. */
}

@keyframes speakers-grid-card-pop {
    from { transform: scale(0.5); opacity: 0; }
    to   { transform: scale(1);   opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
    .speakers-grid-card:not(.is-visible),
    .speakers-grid-card.is-visible {
        animation: none;
        transform: none;
        opacity: 1;
    }
}

.speakers-grid-card .speaker-hex-link {
    mask-image: var(--speaker-hex-mask, url('/img/2026/speakers/speaker-hex.svg'));
    -webkit-mask-image: var(--speaker-hex-mask, url('/img/2026/speakers/speaker-hex.svg'));
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
}
</style>
